import { useEffect, useMemo, useState } from "react";
import { useGetProjects } from "src/domains/projects-list/project-query/projects.queries";
import {
  useGetUsersOfProject,
  usePatchUsersOfProject,
  useCreateUserOfProject,
  useDeleteUserOfProject,
  usersOfProjectQueryKey,
} from "src/domains/myPageContent/userManage/userManage-query/usersOfProject.queries";
import {
  ProjectId,
  Role,
} from "src/domains/myPageContent/userManage/userManage-query/usersOfProject.modal";
import { useQueryClient } from "@tanstack/react-query";
import notice from "src/domains/shared/utils/notice";
import { useTranslation } from "react-i18next";

interface ProjectOption {
  label: string;
  value: string;
}

/**
 * 프로젝트 멤버 관리 — 소유 프로젝트 선택·멤버 목록·권한 변경·추가·삭제. 소유권 이전은 소비자가 겹치지 않아
 * `useProjectOwnerTransfer`(같은 `selectedProject` 를 받는다)가 따로 든다.
 */
export const useUserManagement = () => {
  const { t } = useTranslation("projects");
  const [selectedProject, setSelectedProject] = useState("");
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [editTargetUsers, setEditTargetUsers] = useState<
    { id: number; email: string; role: Role }[]
  >([]);
  const [emailError, setEmailError] = useState<string>("");

  // API 훅들
  const queryClient = useQueryClient();

  // 위조 placeholderData(실패 봉투 흉내)를 제거했다 — 첫 렌더부터 isLoading 을 false 로
  // 만들어 로딩 표시를 죽였고, 소비처는 위조본과 실제 결과를 구별할 수 없었다.
  // 조회 실패는 이제 경계 승격을 거쳐 isError 로만 나타난다.
  const {
    data: ownProjects,
    isError: isOwnProjectsError,
    status: ownProjectsStatus,
    fetchStatus: ownProjectsFetchStatus,
    refetch: refetchOwnProjects,
  } = useGetProjects({ isOwn: true });

  const projectOptions: ProjectOption[] = useMemo(
    () =>
      (ownProjects ?? []).map((project) => ({
        label: project.title,
        value: project.id.toString(),
      })),
    [ownProjects]
  );

  const {
    data: usersData,
    isError: isMembersError,
    status: membersStatus,
    fetchStatus: membersFetchStatus,
    refetch: refetchUsers,
  } = useGetUsersOfProject({
    projectId: selectedProject as ProjectId,
  });

  const { mutate: patchUsers, isLoading: isPatching } =
    usePatchUsersOfProject();
  const { mutate: createUser, isLoading: isCreating } = useCreateUserOfProject({
    projectId: selectedProject as ProjectId,
  });
  const { mutate: deleteUser } = useDeleteUserOfProject();

  // projectOptions가 변경될 때 selectedProject 업데이트
  useEffect(() => {
    if (
      projectOptions.length > 0 &&
      !projectOptions.find((p) => p.value === selectedProject)
    ) {
      setSelectedProject(projectOptions[0].value);
    }
  }, [projectOptions, selectedProject]);

  // 권한 변경 핸들러
  const handleRoleChange = (userId: number, newRole: Role) => {
    const user = usersData?.find((u) => u.id === userId);
    if (user) {
      if (newRole === user.role) {
        setEditTargetUsers((prev) =>
          prev.filter((u) => u.email !== user.email)
        );
      } else {
        setEditTargetUsers((prev) => [
          ...prev,
          { id: user.id, email: user.email, role: newRole },
        ]);
      }
    }
  };

  // 멤버 삭제 핸들러
  const handleDeleteMember = (userId: number) => {
    const user = usersData?.find((u) => u.id === userId);
    if (user) {
      deleteUser(
        {
          projectId: selectedProject as ProjectId,
          email: user.email,
          status: user.status,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: usersOfProjectQueryKey(selectedProject as ProjectId),
            });
            notice("success", t("myPage.messages.userDeleted", { ns: "projects" }));
          },
          onError: () => {
            notice("error", t("myPage.messages.userDeleteFailed", { ns: "projects" }));
          },
        }
      );
    }
  };

  // 멤버 추가 핸들러
  const handleAddMember = (data: { email: string }) => {
    setEmailError("");
    createUser(
      { projectId: selectedProject as ProjectId, email: data.email },
      {
        onSuccess: () => {
          queryClient.refetchQueries({
            queryKey: usersOfProjectQueryKey(selectedProject as ProjectId),
          });
          notice(
            "success",
            t("myPage.messages.userAdded", { ns: "projects" })
          );
          setShowAddMemberDialog(false);
        },
        onError: (error: any) => {
          if (error.response?.status === 409) {
            setEmailError(t("myPageModal.permission.emailExists", { ns: "projects" }));
          } else if (error.response?.status === 400) {
            setEmailError(t("myPageModal.permission.serverError", { ns: "projects" }));
          } else {
            setEmailError(
              t("myPage.messages.userAddFailed", { ns: "projects" })
            );
          }
        },
      }
    );
  };

  // 권한 수정 저장
  const handleSaveChanges = () => {
    if (editTargetUsers.length === 0) return;

    const patchData = editTargetUsers.map((u) => ({
      id: u.id,
      role: u.role,
    }));

    patchUsers(
      {
        projectId: selectedProject as ProjectId,
        patchData,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: usersOfProjectQueryKey(selectedProject as ProjectId),
          });
          notice(
            "success",
            t("myPage.messages.permissionUpdated", { ns: "projects" })
          );
          setEditTargetUsers([]);
        },
        onError: () => {
          notice(
            "error",
            t("myPage.messages.permissionUpdateFailed", { ns: "projects" })
          );
        },
      }
    );
  };

  // 변경사항 초기화
  const handleResetChanges = () => {
    setEditTargetUsers([]);
    refetchUsers();
    notice("success", t("myPageModal.permission.resetDone", { ns: "projects" }));
  };

  // 다이얼로그 열기/닫기 핸들러
  const handleOpenAddDialog = () => {
    setEmailError("");
    setShowAddMemberDialog(true);
  };

  const handleCloseAddDialog = () => {
    setShowAddMemberDialog(false);
    setEmailError("");
  };

  return {
    // State
    selectedProject,
    setSelectedProject,
    showAddMemberDialog,
    editTargetUsers,
    emailError,
    setEmailError,

    // API Data
    projectOptions,
    usersData,

    // Mutation 진행 상태 — 버튼 비활성/라벨 전환용.
    // 조회 쪽 `isLoading`/`isFetching` 은 의도적으로 노출하지 않는다: 갈래 판정을
    // `isLoading` 단독으로 내리면 비활성 쿼리(v4 는 idle 이 없어 영원히 loading 이다)와
    // 실패가 모두 빈 상태로 새어 나간다. 소비처는 아래 쿼리 번들 + itemCount 로만 판정한다.
    isPatching,
    isCreating,

    // 렌더 갈래 판정 원자료 — `resolveQueryRenderState` 에 itemCount 와 함께 넘긴다.
    // 조회 실패를 "소유 프로젝트 없음"·"멤버 없음"으로 번역하지 않기 위해 필요하다.
    // `hasData` 를 함께 싣는 이유: 소유 프로젝트 0개·멤버 0명은 정상 상태라, 항목 수로
    // 2단 가드를 가르면 이미 확인한 "0건"을 배경 재조회 실패 한 번에 오류 화면이 덮는다
    // (이 쿼리는 `refetchOnWindowFocus: true` 라 포커스 복귀만으로 그 경로가 열린다).
    ownProjectListQuery: {
      status: ownProjectsStatus,
      fetchStatus: ownProjectsFetchStatus,
      isError: isOwnProjectsError,
      refetch: refetchOwnProjects,
      hasData: ownProjects !== undefined,
    },
    memberListQuery: {
      status: membersStatus,
      fetchStatus: membersFetchStatus,
      isError: isMembersError,
      refetch: refetchUsers,
      hasData: usersData !== undefined,
    },

    // Actions
    handleRoleChange,
    handleDeleteMember,
    handleAddMember,
    handleSaveChanges,
    handleResetChanges,
    handleOpenAddDialog,
    handleCloseAddDialog,
  };
};
